import React, { useCallback, useEffect, useState } from 'react'
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '../ui/Dialog'
import { Input } from '../ui/Input'
import { Button } from '../ui/Button'
import { useFoldersData } from '@/hooks/UseFoldersData.ts'

interface NewFolderDialogProps {
	isOpen: boolean
	onClose: () => void
}

export function NewFolderDialog({ isOpen, onClose }: NewFolderDialogProps) {
	const [folderName, setFolderName] = useState('')
	const [nameError, setNameError] = useState('')
	const [isCreating, setIsCreating] = useState(false)
	const { createFolder } = useFoldersData()

	// Clear input when dialog opens
	useEffect(() => {
		if (isOpen) {
			setFolderName('')
			setNameError('')
			setIsCreating(false)
		}
	}, [isOpen])

	// Validate folder name
	useEffect(() => {
		if (folderName.length === 0) {
			setNameError('')
		} else if (folderName.trim().length === 0) {
			setNameError('Folder name cannot be empty')
		} else if (folderName.trim().length > 255) {
			setNameError('Folder name must be 255 characters or less')
		} else {
			setNameError('')
		}
	}, [folderName])

	const handleCreate = useCallback(async () => {
		const trimmedName = folderName.trim()

		// Validate before creating
		if (!trimmedName) {
			setNameError('Folder name is required')
			return
		}
		if (trimmedName.length > 255) {
			setNameError('Folder name must be 255 characters or less')
			return
		}

		setIsCreating(true)
		try {
			await createFolder(trimmedName)
			// Clear input and close on success
			setFolderName('')
			setNameError('')
			onClose()
		} catch (error) {
			console.error('Failed to create folder:', error)
			setNameError('Failed to create folder. Please try again.')
		} finally {
			setIsCreating(false)
		}
	}, [folderName, createFolder, onClose])

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === 'Enter' && !isCreating && isFormValid) {
				handleCreate()
			}
		},
		[handleCreate, isCreating],
	)

	const handleOpenChange = useCallback(
		(open: boolean) => {
			if (!open && !isCreating) {
				setFolderName('')
				setNameError('')
				onClose()
			}
		},
		[onClose, isCreating],
	)

	const isFormValid = folderName.trim().length > 0 && folderName.trim().length <= 255 && !nameError

	return (
		<Dialog open={isOpen} onOpenChange={handleOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>New Folder</DialogTitle>
				</DialogHeader>
				<div className="form-field">
					<label htmlFor="folder-name" className="form-label">
						Folder Name
					</label>
					<Input
						id="folder-name"
						type="text"
						value={folderName}
						onChange={e => setFolderName(e.target.value)}
						onKeyDown={handleKeyDown}
						placeholder="Enter folder name"
						maxLength={255}
						disabled={isCreating}
						className={nameError ? 'border-destructive' : ''}
						aria-invalid={!!nameError}
						autoFocus
					/>
					{nameError && (
						<p className="form-error">{nameError}</p>
					)}
				</div>
				<DialogFooter>
					<Button
						variant="ghost"
						onClick={onClose}
						disabled={isCreating}
					>
						Cancel
					</Button>
					<Button
						variant="action"
						onClick={handleCreate}
						disabled={!isFormValid || isCreating}
					>
						{isCreating ? 'Creating...' : 'Create'}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
